// src/utils/docApiClient.js

import { getAuth } from "firebase/auth";

/*
 * Authoritative DocWriter API client.
 *
 * Main-service flow:
 *   POST /docwriter/jobs
 *   POST /docwriter/jobs/:jobId/sources/upload-url
 *   PUT  signed GCS URL directly from the browser
 *   POST /docwriter/jobs/:jobId/sources/:sourceId/complete
 *   POST /docwriter/jobs/:jobId/generate
 *   GET  /docwriter/jobs/:jobId
 *   GET  /docwriter/jobs/:jobId/revisions/:revisionId
 *   GET  the exact signed GCS object returned for that revision generation
 *   POST /docwriter/jobs/:jobId/preview-pdf
 *   POST /docwriter/jobs/:jobId/finalize
 *   GET  /docwriter/history
 *   GET  /docwriter/history/stats
 *   GET  /docwriter/history/:sessionId
 *   PATCH /docwriter/history/:sessionId/rename
 *   PATCH /docwriter/history/:sessionId/archive
 *   PATCH /docwriter/history/:sessionId/restore
 *   DELETE /docwriter/history/:sessionId
 *
 * There is no streaming or non-stream fallback. Draft and revision requests
 * enqueue Cloud Tasks and return JSON immediately.
 */

const API_HOST = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const DOCWRITER_BASE = `${API_HOST}/docwriter`;

const DEFAULT_JSON_TIMEOUT_MS = 45_000;
const DEFAULT_UPLOAD_TIMEOUT_MS = 10 * 60_000;
const DEFAULT_REVISION_CONTENT_TIMEOUT_MS = 45_000;
const DEFAULT_PDF_PREVIEW_TIMEOUT_MS = 90_000;
const MAX_REVISION_INSTRUCTION_CHARS = 12_000;
const MAX_REVISION_CONTENT_CACHE_ENTRIES = 8;
const CLIENT_HEADER = "docwriter-ui/2.1";

const DOCWRITER_GENERIC_USER_ERROR =
  "An error occurred while preparing your document. Please try again later.";

const DOCWRITER_GENERIC_UPLOAD_ERROR =
  "We couldn't upload the selected file. Please try again.";

const DOCWRITER_PREFLIGHT_START_OVER =
  "Please start a new Doc Writer job after correcting the source file or package.";

const DOCWRITER_DETERMINISTIC_PREFLIGHT_CODES = new Set([
  "too_many_active_sources",
  "empty_file",
  "unsupported_extension",
  "unsupported_actual_type",
  "mime_mismatch",
  "file_too_large",
  "combined_bytes_exceeded",
  "corrupt_file",
  "encrypted_file",
  "page_limit_exceeded",
  "combined_page_limit_exceeded",
]);

const DOCWRITER_PREFLIGHT_CODE_PRIORITY = [
  "combined_page_limit_exceeded",
  "page_limit_exceeded",
  "combined_bytes_exceeded",
  "file_too_large",
  "too_many_active_sources",
  "encrypted_file",
  "corrupt_file",
  "unsupported_actual_type",
  "unsupported_extension",
  "mime_mismatch",
  "empty_file",
];

const SESSION_JOB_PREFIX = "docwriter:job-by-session:";
const JOB_SESSION_PREFIX = "docwriter:session-by-job:";

function newRequestId(prefix = "docwriter") {
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`;

  return `${prefix}_${random}`;
}

function safeString(value) {
  return String(value || "").trim();
}

function safeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safePositiveInt(value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    const fallbackNumber = Number(fallback);
    return Number.isFinite(fallbackNumber) && fallbackNumber > 0
      ? Math.trunc(fallbackNumber)
      : 0;
  }
  return Math.trunc(numeric);
}

function normalizeValidationCode(value) {
  return safeString(value).toLowerCase();
}

function sourceRecordsFromJob(jobLike) {
  const job = safeObject(jobLike);
  const sources = job.sources;

  if (Array.isArray(sources)) {
    return sources.map((source) => safeObject(source));
  }

  if (sources && typeof sources === "object") {
    return Object.entries(sources).map(([sourceId, sourceLike]) => ({
      sourceId,
      ...safeObject(sourceLike),
    }));
  }

  return [];
}

function validationCodesFromJob(jobLike) {
  const job = safeObject(jobLike);
  const preflight = safeObject(job.preflightFailure);
  const packageValidation = safeObject(job.packageValidation);
  const sourceValidation = safeObject(job.sourceValidation);
  const abandonment = safeObject(job.abandonment);

  const candidates = [
    preflight.primaryValidationCode,
    packageValidation.code,
    packageValidation.validationCode,
    abandonment.primaryValidationCode,
    ...safeArray(preflight.validationCodes),
    ...safeArray(packageValidation.codes),
    ...safeArray(packageValidation.packageValidationCodes),
    ...safeArray(sourceValidation.validationCodes),
    ...safeArray(sourceValidation.deterministicValidationCodes),
  ];

  for (const source of sourceRecordsFromJob(job)) {
    candidates.push(source.validationCode);
  }

  return Array.from(
    new Set(candidates.map(normalizeValidationCode).filter(Boolean))
  );
}

function primaryDeterministicPreflightCode(jobLike) {
  const codes = new Set(validationCodesFromJob(jobLike));
  return (
    DOCWRITER_PREFLIGHT_CODE_PRIORITY.find((code) => codes.has(code)) ||
    Array.from(codes).find((code) =>
      DOCWRITER_DETERMINISTIC_PREFLIGHT_CODES.has(code)
    ) ||
    ""
  );
}

function preflightInvalidSources(jobLike) {
  const job = safeObject(jobLike);
  const preflight = safeObject(job.preflightFailure);
  const fromPreflight = safeArray(preflight.invalidSources)
    .map((source) => safeObject(source))
    .filter((source) => Object.keys(source).length);

  if (fromPreflight.length) return fromPreflight;

  const invalidIds = new Set(
    safeArray(preflight.invalidSourceIds)
      .map((value) => safeString(value))
      .filter(Boolean)
  );

  return sourceRecordsFromJob(job).filter((source) => {
    const validationCode = normalizeValidationCode(source.validationCode);
    const validationState = safeString(
      source.validationState || source.state
    ).toLowerCase();
    const sourceId = safeString(source.sourceId || source.id);

    return (
      validationState === "invalid" ||
      DOCWRITER_DETERMINISTIC_PREFLIGHT_CODES.has(validationCode) ||
      (sourceId && invalidIds.has(sourceId))
    );
  });
}

function sourceDisplayName(sourceLike) {
  const source = safeObject(sourceLike);
  return (
    safeString(
      source.filename ||
        source.originalFilename ||
        source.name ||
        source.fileName
    ) || "The selected source file"
  );
}

function sourceForValidationCode(jobLike, code) {
  const normalizedCode = normalizeValidationCode(code);
  const invalidSources = preflightInvalidSources(jobLike);

  return (
    invalidSources.find(
      (source) =>
        normalizeValidationCode(source.validationCode) === normalizedCode
    ) ||
    invalidSources[0] ||
    {}
  );
}

function buildPreflightUserMessage(jobLike, codeLike) {
  const job = safeObject(jobLike);
  const preflight = safeObject(job.preflightFailure);
  const packageValidation = safeObject(job.packageValidation);
  const sourceValidation = safeObject(job.sourceValidation);
  const code = normalizeValidationCode(codeLike);
  const source = sourceForValidationCode(job, code);
  const filename = sourceDisplayName(source);

  const perFileLimit =
    safePositiveInt(
      source.maximumPagesPerFile ||
        source.maximumActualPages ||
        sourceValidation.maximumPagesPerFile
    ) || 30;

  const combinedLimit =
    safePositiveInt(
      packageValidation.maximumCombinedPages ||
        packageValidation.maximumActualPages ||
        sourceValidation.maximumCombinedPages
    ) || 55;

  const actualSourcePages = safePositiveInt(
    source.actualPageCount || source.pageCount
  );
  const totalActualPages = safePositiveInt(
    preflight.totalActualPages ||
      packageValidation.totalActualPages ||
      packageValidation.actualCombinedPageCount ||
      sourceValidation.totalActualPages ||
      sourceValidation.actualCombinedPageCount
  );

  switch (code) {
    case "page_limit_exceeded":
      return actualSourcePages
        ? `“${filename}” contains ${actualSourcePages} pages. Doc Writer supports up to ${perFileLimit} pages per source file. ${DOCWRITER_PREFLIGHT_START_OVER}`
        : `“${filename}” exceeds the Doc Writer page limit of ${perFileLimit} pages per source file. ${DOCWRITER_PREFLIGHT_START_OVER}`;

    case "combined_page_limit_exceeded":
      return totalActualPages
        ? `Your supporting files contain ${totalActualPages} pages in total. Doc Writer supports up to ${combinedLimit} pages per source package. ${DOCWRITER_PREFLIGHT_START_OVER}`
        : `Your supporting files exceed the Doc Writer limit of ${combinedLimit} pages per source package. ${DOCWRITER_PREFLIGHT_START_OVER}`;

    case "encrypted_file":
      return `“${filename}” is password-protected or encrypted, so Doc Writer cannot read it safely. Please start a new Doc Writer job and upload an unlocked copy.`;

    case "corrupt_file":
      return `“${filename}” could not be read because the file appears damaged or incomplete. Please start a new Doc Writer job and upload a valid copy.`;

    case "unsupported_extension":
    case "unsupported_actual_type":
      return `“${filename}” is not a supported file type for Doc Writer. Please start a new Doc Writer job and upload a supported file such as PDF, DOCX, XLSX, TXT/CSV, or a supported image.`;

    case "mime_mismatch":
      return `“${filename}” does not match its reported file type, so Doc Writer could not verify it safely. Please start a new Doc Writer job using the original file or export it again in a supported format.`;

    case "empty_file":
      return `“${filename}” appears to be empty. Please start a new Doc Writer job and upload a file that contains readable content.`;

    case "file_too_large":
      return `“${filename}” exceeds the allowed Doc Writer upload size. Please start a new Doc Writer job with a smaller file.`;

    case "combined_bytes_exceeded":
      return `The combined size of your supporting files exceeds the allowed Doc Writer upload size. Please start a new Doc Writer job with a smaller source package.`;

    case "too_many_active_sources":
      return `Too many supporting files were included in this Doc Writer job. Please start a new Doc Writer job with fewer source files.`;

    default:
      return `One or more supporting files could not be accepted by Doc Writer. ${DOCWRITER_PREFLIGHT_START_OVER}`;
  }
}

function isAbandonedPreflightJob(jobLike) {
  const job = safeObject(jobLike);
  const abandonment = safeObject(job.abandonment);
  const preflight = safeObject(job.preflightFailure);
  const state = safeString(
    job.documentState || job.status || abandonment.state
  ).toLowerCase();

  return Boolean(
    preflight.rejected === true ||
      (
        (job.abandoned === true ||
          abandonment.abandoned === true ||
          state === "abandoned" ||
          state === "awaiting_source_correction") &&
        (
          safeString(abandonment.source).toLowerCase() === "worker_preflight" ||
          Boolean(primaryDeterministicPreflightCode(job))
        )
      )
  );
}

export function getDocwriterUserNotice(jobLike) {
  const job = safeObject(jobLike);
  const abandonment = safeObject(job.abandonment);
  const state = safeString(
    job.documentState || job.status || abandonment.state
  ).toLowerCase();

  if (isAbandonedPreflightJob(job)) {
    const code = primaryDeterministicPreflightCode(job);
    return {
      visible: true,
      terminal: state === "abandoned" || job.abandoned === true,
      kind: "source_rejection",
      action: "start_over",
      code: code || undefined,
      title: "Source file needs attention",
      message: buildPreflightUserMessage(job, code),
    };
  }

  if (
    state === "error" ||
    state === "failed" ||
    state === "cancelled" ||
    state === "canceled"
  ) {
    return {
      visible: true,
      terminal: true,
      kind: "system_error",
      action: "retry_later",
      title: "We couldn't complete your document",
      message: DOCWRITER_GENERIC_USER_ERROR,
    };
  }

  return {
    visible: false,
    terminal: false,
    kind: "",
    action: "",
    title: "",
    message: "",
  };
}

function userFacingHttpErrorMessage(status, codeLike, technicalMessage = "") {
  const code = normalizeValidationCode(codeLike);

  if (DOCWRITER_DETERMINISTIC_PREFLIGHT_CODES.has(code)) {
    return buildPreflightUserMessage(
      {
        preflightFailure: {
          rejected: true,
          primaryValidationCode: code,
        },
      },
      code
    );
  }

  if (Number(status) === 401) {
    return "Your sign-in session could not be verified. Please sign in again and retry.";
  }

  if (Number(status) === 402) {
    return "Doc Writer is not available for this request. Please check your document balance or plan and try again.";
  }

  if (Number(status) === 403) {
    return "This Doc Writer request is not available for your account right now. Please try again or return to Doc Writer.";
  }

  void technicalMessage;
  return DOCWRITER_GENERIC_USER_ERROR;
}

const revisionContentCache = new Map();

function normalizeRevisionId(value) {
  const normalized = safeString(value).toUpperCase();
  return /^(?:D1|R[1-9]\d*)$/.test(normalized) ? normalized : "";
}

function revisionSortKey(value) {
  const revisionId = normalizeRevisionId(value);
  if (revisionId === "D1") return [0, 0];
  const number = Number(revisionId.slice(1));
  return [1, Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER];
}

function compareRevisionIds(left, right) {
  const a = revisionSortKey(left);
  const b = revisionSortKey(right);
  return a[0] - b[0] || a[1] - b[1];
}

function normalizeRevisionInstruction(value) {
  const instruction = safeString(value);

  if (!instruction) return "";

  if (instruction.length > MAX_REVISION_INSTRUCTION_CHARS) {
    throw new Error(
      `Revision instructions cannot exceed ${MAX_REVISION_INSTRUCTION_CHARS} characters.`
    );
  }

  const lowered = instruction.toLowerCase();
  const embeddedDocumentMarkers = [
    "=== current_draft_html",
    "current_draft_html:",
    "=== current_draft_text",
    "current_draft_text:",
    "=== current_document_html",
    "current_document_html:",
    "=== current_document_text",
    "current_document_text:",
    "<!doctype",
    "<html",
    "<body",
    "<article",
  ];

  if (embeddedDocumentMarkers.some((marker) => lowered.includes(marker))) {
    throw new Error(
      "Revision instructions must not contain the current document HTML or full document text."
    );
  }

  return instruction;
}

function revisionIndexFromEnvelope(value) {
  const envelope = safeObject(value);
  const job = safeObject(envelope.job);
  return safeObject(envelope.revisionIndex || job.revisionIndex);
}

function availableRevisionIdsFromEnvelope(value) {
  const envelope = safeObject(value);
  const job = safeObject(envelope.job);
  const revisionState = safeObject(job.revisionState);
  const revisionIndex = revisionIndexFromEnvelope(envelope);
  const records = safeObject(revisionIndex.revisions);

  const ids = [
    ...(Array.isArray(revisionIndex.availableRevisionIds)
      ? revisionIndex.availableRevisionIds
      : []),
    ...(Array.isArray(revisionState.availableRevisionIds)
      ? revisionState.availableRevisionIds
      : []),
    ...Object.keys(records),
  ]
    .map(normalizeRevisionId)
    .filter(Boolean);

  return Array.from(new Set(ids)).sort(compareRevisionIds);
}

function pointerIdentity(pointerLike) {
  const pointer = safeObject(pointerLike);
  return [
    safeString(pointer.bucket),
    safeString(pointer.objectPath || pointer.object_path),
    safeString(pointer.generation),
  ].join(":");
}

function normalizeRevisionPointer(
  pointerLike,
  {
    fieldName,
    revisionId,
    expectedFilename = "",
    requireReadUrl = false,
  } = {}
) {
  const pointer = safeObject(pointerLike);
  const bucket = safeString(pointer.bucket);
  const objectPath = safeString(pointer.objectPath || pointer.object_path);
  const generation = safeString(pointer.generation);
  const readUrl = safeString(pointer.readUrl || pointer.read_url || pointer.url);
  const normalizedRevisionId = normalizeRevisionId(revisionId);

  if (!bucket || !objectPath || !generation) {
    throw new Error(`${fieldName || "Revision pointer"} is incomplete.`);
  }

  if (!normalizedRevisionId) {
    throw new Error("The requested DocWriter revision ID is invalid.");
  }

  const expectedSegment = `/revisions/${normalizedRevisionId}/`;
  if (!`/${objectPath}`.includes(expectedSegment)) {
    throw new Error(
      `${fieldName || "Revision pointer"} does not belong to ${normalizedRevisionId}.`
    );
  }

  if (expectedFilename && !objectPath.endsWith(`/${expectedFilename}`)) {
    throw new Error(
      `${fieldName || "Revision pointer"} does not point to ${expectedFilename}.`
    );
  }

  if (requireReadUrl && !/^https?:\/\//i.test(readUrl)) {
    throw new Error(`${fieldName || "Revision pointer"} has no valid signed read URL.`);
  }

  return {
    ...pointer,
    bucket,
    objectPath,
    generation,
    ...(readUrl ? { readUrl } : {}),
  };
}

function assertSamePointer(left, right, fieldName) {
  if (pointerIdentity(left) !== pointerIdentity(right)) {
    throw new Error(`${fieldName} does not match the canonical stored pointer.`);
  }
}

function normalizeExactRevisionEnvelope(data, expectedRevisionId) {
  const envelope = safeObject(data);
  const revision = safeObject(envelope.revision);
  const normalizedExpected = normalizeRevisionId(expectedRevisionId);
  const envelopeRequestedRevisionId = normalizeRevisionId(
    envelope.requestedRevisionId
  );
  const revisionRequestedRevisionId = normalizeRevisionId(
    revision.requestedRevisionId
  );
  const returnedRevisionId = normalizeRevisionId(
    revision.revisionId || envelope.revisionId
  );

  if (!normalizedExpected) {
    throw new Error("The requested DocWriter revision ID is invalid.");
  }

  if (
    envelopeRequestedRevisionId !== normalizedExpected ||
    revisionRequestedRevisionId !== normalizedExpected ||
    returnedRevisionId !== normalizedExpected
  ) {
    throw new Error(
      `The server returned a different revision than ${normalizedExpected}.`
    );
  }

  const artifacts = safeObject(revision.artifacts);
  const storedHtml = normalizeRevisionPointer(
    artifacts.html,
    {
      fieldName: "Stored HTML pointer",
      revisionId: normalizedExpected,
      expectedFilename: "content.html",
    }
  );
  const storedText = normalizeRevisionPointer(artifacts.text, {
    fieldName: "Stored text pointer",
    revisionId: normalizedExpected,
    expectedFilename: "content.txt",
  });
  const storedMetadata = normalizeRevisionPointer(
    artifacts.metadata,
    {
      fieldName: "Stored metadata pointer",
      revisionId: normalizedExpected,
      expectedFilename: "metadata.json",
    }
  );
  const signedHtml = normalizeRevisionPointer(revision.html, {
    fieldName: "Signed HTML pointer",
    revisionId: normalizedExpected,
    expectedFilename: "content.html",
    requireReadUrl: true,
  });
  const signedText = normalizeRevisionPointer(revision.text, {
    fieldName: "Signed text pointer",
    revisionId: normalizedExpected,
    expectedFilename: "content.txt",
    requireReadUrl: true,
  });
  const metadataPointer = normalizeRevisionPointer(revision.metadataPointer, {
    fieldName: "Revision metadata pointer",
    revisionId: normalizedExpected,
    expectedFilename: "metadata.json",
  });
  const directHtmlPointer = normalizeRevisionPointer(
    revision.pointer,
    {
      fieldName: "Selected HTML pointer",
      revisionId: normalizedExpected,
      expectedFilename: "content.html",
    }
  );

  assertSamePointer(signedHtml, storedHtml, "Signed HTML pointer");
  assertSamePointer(signedText, storedText, "Signed text pointer");
  assertSamePointer(metadataPointer, storedMetadata, "Metadata pointer");
  assertSamePointer(directHtmlPointer, storedHtml, "Selected HTML pointer");

  const buckets = new Set(
    [
      signedHtml.bucket,
      signedText.bucket,
      storedHtml.bucket,
      storedText.bucket,
      storedMetadata.bucket,
      metadataPointer.bucket,
    ].filter(Boolean)
  );

  if (buckets.size !== 1) {
    throw new Error("The selected revision pointers do not use one canonical bucket.");
  }

  return {
    ...envelope,
    requestedRevisionId: normalizedExpected,
    revisionId: normalizedExpected,
    revision: {
      ...revision,
      requestedRevisionId: normalizedExpected,
      revisionId: normalizedExpected,
      html: signedHtml,
      text: signedText,
      metadataPointer,
      pointer: directHtmlPointer,
      artifacts: {
        ...artifacts,
        html: storedHtml,
        text: storedText,
        metadata: storedMetadata,
      },
    },
    pointers: {
      html: storedHtml,
      text: storedText,
      metadata: storedMetadata,
    },
    signedReads: {
      html: signedHtml,
      text: signedText,
    },
  };
}

function setRevisionContentCache(cacheKey, value) {
  if (!cacheKey || typeof value !== "string") return;

  revisionContentCache.delete(cacheKey);
  revisionContentCache.set(cacheKey, value);

  while (revisionContentCache.size > MAX_REVISION_CONTENT_CACHE_ENTRIES) {
    const oldestKey = revisionContentCache.keys().next().value;
    revisionContentCache.delete(oldestKey);
  }
}

async function fetchSignedRevisionContent(
  pointerLike,
  {
    fieldName = "Revision content",
    timeoutMs = DEFAULT_REVISION_CONTENT_TIMEOUT_MS,
    accept = "text/plain, text/html;q=0.9, */*;q=0.1",
  } = {}
) {
  const pointer = safeObject(pointerLike);
  const readUrl = safeString(pointer.readUrl);
  const cacheKey = pointerIdentity(pointer);

  if (!readUrl) {
    throw new Error(`${fieldName} has no signed read URL.`);
  }

  if (cacheKey && revisionContentCache.has(cacheKey)) {
    return revisionContentCache.get(cacheKey);
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), Math.max(1, Number(timeoutMs)));

  try {
    const response = await fetch(readUrl, {
      method: "GET",
      credentials: "omit",
      cache: "no-store",
      headers: { Accept: accept },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`${fieldName} could not be read (HTTP ${response.status}).`);
    }

    const content = await response.text();
    setRevisionContentCache(cacheKey, content);
    return content;
  } catch (error) {
    if (error?.name === "AbortError") {
      throw new Error(`${fieldName} timed out.`);
    }

    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

async function hydrateExactRevisionEnvelope(
  normalizedEnvelope,
  { includeText = false, timeoutMs = DEFAULT_REVISION_CONTENT_TIMEOUT_MS } = {}
) {
  const envelope = safeObject(normalizedEnvelope);
  const revision = safeObject(envelope.revision);
  const signedReads = safeObject(envelope.signedReads);

  const htmlPromise = fetchSignedRevisionContent(signedReads.html, {
    fieldName: `${envelope.revisionId} HTML`,
    timeoutMs,
    accept: "text/html, application/xhtml+xml;q=0.9, text/plain;q=0.8",
  });
  const textPromise = includeText
    ? fetchSignedRevisionContent(signedReads.text, {
        fieldName: `${envelope.revisionId} text`,
        timeoutMs,
        accept: "text/plain, */*;q=0.1",
      })
    : Promise.resolve("");

  const [contentHtml, contentText] = await Promise.all([htmlPromise, textPromise]);

  if (!safeString(contentHtml)) {
    throw new Error(`${envelope.revisionId} returned empty HTML content.`);
  }

  return {
    ...envelope,
    contentHtml,
    htmlContent: contentHtml,
    ...(contentText ? { contentText, textContent: contentText } : {}),
    revision: {
      ...revision,
      contentHtml,
      htmlContent: contentHtml,
      ...(contentText ? { contentText, textContent: contentText } : {}),
    },
  };
}

function errorResult(code, message, extra = {}) {
  return {
    ok: false,
    error: {
      code: safeString(code) || "UNKNOWN",
      message: safeString(message) || "DocWriter request failed.",
      ...extra,
    },
  };
}

async function resolveIdToken(idTokenMaybe) {
  if (safeString(idTokenMaybe)) return safeString(idTokenMaybe);

  try {
    const user = getAuth()?.currentUser;
    if (!user) return "";
    return (await user.getIdToken(false)) || "";
  } catch {
    return "";
  }
}

function buildHeaders(idToken, extra = {}) {
  return {
    "Content-Type": "application/json",
    "X-Client": CLIENT_HEADER,
    ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
    ...extra,
  };
}

function normalizeError(status, data) {
  const body = safeObject(data);
  const nested = safeObject(body.error);
  const billing = safeObject(body.billing);
  const isBilling = status === 402 || status === 403;

  const technicalMessage =
    safeString(nested.message) ||
    (typeof body.error === "string" ? safeString(body.error) : "") ||
    safeString(body.message) ||
    `DocWriter request failed (HTTP ${status}).`;

  const technicalCode = safeString(
    nested.code || body.code || (isBilling ? "BILLING_FORBIDDEN" : status)
  );
  const reason =
    safeString(nested.reason) ||
    safeString(body.reason) ||
    safeString(billing.reason);

  return errorResult(
    isBilling ? "BILLING_FORBIDDEN" : technicalCode,
    userFacingHttpErrorMessage(status, technicalCode, technicalMessage),
    {
      status,
      reason: reason || undefined,
      billing,
      billingSnapshot:
        nested.billingSnapshot ||
        nested.billing_snapshot ||
        body.billingSnapshot ||
        body.billing_snapshot ||
        billing,
      technicalCode: technicalCode || undefined,
      technicalMessage: technicalMessage || undefined,
      raw: body,
    }
  );
}

async function fetchJson(
  method,
  path,
  body,
  idTokenMaybe,
  { timeoutMs = DEFAULT_JSON_TIMEOUT_MS, headers = {} } = {}
) {
  const idToken = await resolveIdToken(idTokenMaybe);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${DOCWRITER_BASE}${path}`, {
      method,
      headers: buildHeaders(idToken, headers),
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "include",
      signal: controller.signal,
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      return normalizeError(response.status, data);
    }

    return { ok: true, data };
  } catch (error) {
    if (error?.name === "AbortError") {
      return errorResult("TIMEOUT", DOCWRITER_GENERIC_USER_ERROR, {
        technicalMessage: "The DocWriter request timed out.",
      });
    }

    return errorResult("NETWORK", DOCWRITER_GENERIC_USER_ERROR, {
      technicalMessage: error?.message || "Network error.",
    });
  } finally {
    clearTimeout(timeout);
  }
}


function filenameFromContentDisposition(value, fallback = "docwriter-preview.pdf") {
  const disposition = safeString(value);
  if (!disposition) return fallback;

  const utf8Match = disposition.match(
    /filename\*\s*=\s*UTF-8''([^;]+)/i
  );

  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(
        utf8Match[1].trim().replace(/^["']|["']$/g, "")
      );
    } catch {
      return utf8Match[1].trim().replace(/^["']|["']$/g, "") || fallback;
    }
  }

  const quotedMatch = disposition.match(/filename\s*=\s*"([^"]+)"/i);
  if (quotedMatch?.[1]) return quotedMatch[1].trim() || fallback;

  const plainMatch = disposition.match(/filename\s*=\s*([^;]+)/i);
  return plainMatch?.[1]?.trim().replace(/^["']|["']$/g, "") || fallback;
}

async function errorPayloadFromResponse(response) {
  const contentType = safeString(
    response?.headers?.get?.("content-type")
  ).toLowerCase();

  if (contentType.includes("application/json")) {
    return response.json().catch(() => ({}));
  }

  const text = await response.text().catch(() => "");

  return text
    ? {
        error: text,
        message: text,
      }
    : {};
}

async function fetchPdf(
  path,
  body,
  idTokenMaybe,
  { timeoutMs = DEFAULT_PDF_PREVIEW_TIMEOUT_MS, headers = {} } = {}
) {
  const idToken = await resolveIdToken(idTokenMaybe);
  const controller = new AbortController();
  const timeout = setTimeout(
    () => controller.abort(),
    Math.max(1, Number(timeoutMs || DEFAULT_PDF_PREVIEW_TIMEOUT_MS))
  );

  try {
    const response = await fetch(`${DOCWRITER_BASE}${path}`, {
      method: "POST",
      headers: buildHeaders(idToken, {
        Accept: "application/pdf, application/json;q=0.9, */*;q=0.1",
        ...headers,
      }),
      body: JSON.stringify(safeObject(body)),
      credentials: "include",
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorPayload = await errorPayloadFromResponse(response);
      return normalizeError(response.status, errorPayload);
    }

    const contentType = safeString(
      response.headers.get("content-type")
    ).toLowerCase();

    if (!contentType.includes("application/pdf")) {
      return errorResult(
        "PDF_PREVIEW_INVALID_RESPONSE",
        "The DocWriter preview endpoint did not return a PDF.",
        {
          status: response.status,
          contentType: contentType || undefined,
        }
      );
    }

    const blob = await response.blob();

    if (!blob || blob.size <= 0) {
      return errorResult(
        "PDF_PREVIEW_EMPTY",
        "The DocWriter PDF preview was empty."
      );
    }

    const filename = filenameFromContentDisposition(
      response.headers.get("content-disposition")
    );
    const returnedJobId = safeString(
      response.headers.get("x-docwriter-job-id")
    );
    const returnedRevisionId = normalizeRevisionId(
      response.headers.get("x-docwriter-revision-id")
    );
    const previewRequestId = safeString(
      response.headers.get("x-docwriter-preview-request-id")
    );
    const sha256 = safeString(
      response.headers.get("x-docwriter-pdf-sha256")
    );

    return {
      ok: true,
      data: {
        blob,
        filename,
        contentType: "application/pdf",
        sizeBytes: blob.size,
        jobId: returnedJobId || undefined,
        revisionId: returnedRevisionId || undefined,
        requestId: previewRequestId || undefined,
        sha256: sha256 || undefined,
      },
    };
  } catch (error) {
    if (error?.name === "AbortError") {
      return errorResult(
        "PDF_PREVIEW_TIMEOUT",
        "The DocWriter PDF preview timed out."
      );
    }

    return errorResult(
      "PDF_PREVIEW_NETWORK",
      error?.message || "The DocWriter PDF preview could not be prepared."
    );
  } finally {
    clearTimeout(timeout);
  }
}

function rememberJobSession(jobLike) {
  const job = safeObject(jobLike);
  const jobId = safeString(job.jobId || job.job_id);
  const sessionId = safeString(job.sessionId || job.session_id);

  if (!jobId || !sessionId || typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(`${SESSION_JOB_PREFIX}${sessionId}`, jobId);
    window.sessionStorage.setItem(`${JOB_SESSION_PREFIX}${jobId}`, sessionId);
  } catch {
    // Browser storage is only a convenience. Explicit jobId always wins.
  }
}

export function rememberDocwriterJob(jobLike) {
  rememberJobSession(jobLike);
}

export function getRememberedDocwriterJobId(sessionId) {
  const normalized = safeString(sessionId);
  if (!normalized || typeof window === "undefined") return "";

  try {
    return safeString(
      window.sessionStorage.getItem(`${SESSION_JOB_PREFIX}${normalized}`)
    );
  } catch {
    return "";
  }
}

export function getRememberedDocwriterSessionId(jobId) {
  const normalized = safeString(jobId);
  if (!normalized || typeof window === "undefined") return "";

  try {
    return safeString(
      window.sessionStorage.getItem(`${JOB_SESSION_PREFIX}${normalized}`)
    );
  } catch {
    return "";
  }
}

function resolveJobId(value) {
  if (value && typeof value === "object") {
    const direct = safeString(value.jobId || value.job_id);
    if (direct) return direct;

    const sessionId = safeString(value.sessionId || value.session_id);
    const remembered = getRememberedDocwriterJobId(sessionId);
    if (remembered) return remembered;
  }

  const normalized = safeString(value);
  if (!normalized) return "";

  const remembered = getRememberedDocwriterJobId(normalized);
  return remembered || normalized;
}

function normalizeJobEnvelope(data) {
  const envelope = safeObject(data);
  const job = safeObject(envelope.job);
  const jobId = safeString(job.jobId || job.job_id || envelope.jobId || envelope.job_id);
  const sessionId = safeString(
    job.sessionId ||
      job.session_id ||
      envelope.sessionId ||
      envelope.session_id ||
      envelope.docSessionId ||
      envelope.doc_session_id
  );

  const revisionIndex = safeObject(envelope.revisionIndex || job.revisionIndex);
  const baseJob = {
    ...job,
    ...(jobId ? { jobId } : {}),
    ...(sessionId ? { sessionId } : {}),
    ...(Object.keys(revisionIndex).length ? { revisionIndex } : {}),
  };
  const userNotice = getDocwriterUserNotice(baseJob);
  const normalizedJob = {
    ...baseJob,
    userNotice,
  };

  rememberJobSession(normalizedJob);

  return {
    ...envelope,
    job: normalizedJob,
    jobId,
    sessionId,
    userNotice,
  };
}

function getRawFile(item) {
  if (!item) return null;

  if (typeof File !== "undefined" && item instanceof File) {
    return item;
  }

  const candidates = [
    item.rawFile,
    item.file,
    item.originalFile,
    item.blob,
    item.sourceFile,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;

    if (typeof File !== "undefined" && candidate instanceof File) {
      return candidate;
    }

    if (
      typeof Blob !== "undefined" &&
      candidate instanceof Blob &&
      safeString(candidate.name || item.name)
    ) {
      return candidate;
    }
  }

  return null;
}

function fileMetadata(item, index) {
  const file = getRawFile(item);
  const filename = safeString(file?.name || item?.name || `source-${index + 1}`);
  const contentType = safeString(
    file?.type || item?.type || item?.mime || "application/octet-stream"
  ).toLowerCase();
  const sizeBytes = Number(file?.size ?? item?.size ?? 0);

  return {
    file,
    filename,
    contentType,
    sizeBytes: Number.isFinite(sizeBytes) ? Math.max(0, Math.trunc(sizeBytes)) : 0,
    note: safeString(item?.note),
    clientId: safeString(item?.id),
  };
}


function uploadNowMs() {
  if (
    typeof performance !== "undefined" &&
    typeof performance.now === "function"
  ) {
    return performance.now();
  }
  return Date.now();
}

function uploadElapsedMs(startedAtMs) {
  return Math.max(0, Math.round(uploadNowMs() - Number(startedAtMs || 0)));
}

function emitDocwriterUploadEvent(options, event = {}) {
  const callback =
    typeof options?.onUploadEvent === "function"
      ? options.onUploadEvent
      : typeof options?.onProgress === "function"
      ? options.onProgress
      : null;

  if (!callback) return;

  try {
    callback({
      emittedAt: new Date().toISOString(),
      ...event,
    });
  } catch (error) {
    console.warn("[DocWriter API] Upload progress callback failed:", error);
  }
}

function uploadErrorWithContext(result, context = {}) {
  if (!result || result.ok !== false) return result;

  return {
    ...result,
    error: {
      ...safeObject(result.error),
      ...context,
    },
  };
}


function htmlToPlainText(html) {
  const raw = safeString(html);
  if (!raw) return "";

  if (typeof document !== "undefined") {
    const container = document.createElement("div");
    container.innerHTML = raw;
    return safeString(container.textContent || container.innerText);
  }

  return safeString(raw.replace(/<[^>]*>/g, " "));
}

/* =========================================================================
 * Launcher and job status
 * ========================================================================= */

export async function createDocwriterJob(requestPayload, idToken, options = {}) {
  const response = await fetchJson(
    "POST",
    "/jobs",
    {
      request: safeObject(requestPayload),
      requestId: safeString(options.requestId) || newRequestId("create"),
      ...(safeString(options.jobId) ? { jobId: safeString(options.jobId) } : {}),
      ...(safeString(options.sessionId)
        ? { sessionId: safeString(options.sessionId) }
        : {}),
    },
    idToken
  );

  if (!response.ok) return response;

  return {
    ok: true,
    data: normalizeJobEnvelope(response.data),
  };
}

/* Existing launcher import name retained, but now uses POST /docwriter/jobs. */
export async function createDocSession(payload, idToken) {
  return createDocwriterJob(payload, idToken);
}

export async function getDocwriterJob(jobId, idToken) {
  const resolvedJobId = resolveJobId(jobId);

  if (!resolvedJobId) {
    return errorResult("JOB_ID_REQUIRED", "A DocWriter job ID is required.");
  }

  const response = await fetchJson(
    "GET",
    `/jobs/${encodeURIComponent(resolvedJobId)}`,
    undefined,
    idToken
  );

  if (!response.ok) return response;

  return {
    ok: true,
    data: normalizeJobEnvelope(response.data),
  };
}

/* Existing editor import name retained. */
export async function loadSession(sessionOrJobId, idToken) {
  const jobResponse = await getDocwriterJob(sessionOrJobId, idToken);
  if (!jobResponse.ok) return jobResponse;

  const envelope = jobResponse.data;
  const job = safeObject(envelope.job);
  const revisionState = safeObject(job.revisionState);
  const revisionIndex = revisionIndexFromEnvelope(envelope);
  const currentRevisionId = normalizeRevisionId(
    revisionIndex.currentRevisionId ||
      revisionState.currentRevisionId ||
      revisionIndex.latestRevisionId ||
      revisionState.latestRevisionId
  );

  let revisionEnvelope = {};
  let revision = null;

  if (currentRevisionId) {
    const revisionResponse = await getDocwriterRevision(
      envelope.jobId,
      currentRevisionId,
      idToken
    );

    if (!revisionResponse.ok) return revisionResponse;

    revisionEnvelope = safeObject(revisionResponse.data);
    revision = safeObject(revisionEnvelope.revision);
  }

  return {
    ok: true,
    data: {
      ...envelope,
      ...job,
      job,
      jobId: envelope.jobId,
      sessionId: envelope.sessionId,
      currentRevisionId,
      revision,
      contentHtml:
        revisionEnvelope.contentHtml ||
        revision?.htmlContent ||
        revision?.contentHtml ||
        "",
      contentText:
        revisionEnvelope.contentText ||
        revision?.textContent ||
        revision?.contentText ||
        "",
      revisionCount:
        Number(
          revisionIndex.completedAiRevisionCount ??
            revisionState.completedAiRevisionCount ??
            revisionState.count ??
            0
        ) || 0,
      versionCount:
        Number(
          revisionIndex.versionCount ??
            revisionState.versionCount ??
            availableRevisionIdsFromEnvelope(envelope).length
        ) || 0,
      availableRevisionIds: availableRevisionIdsFromEnvelope(envelope),
    },
  };
}

/* =========================================================================
 * Direct browser-to-GCS source upload
 * ========================================================================= */

export async function requestDocwriterSourceUpload(jobId, sourceItem, idToken, options = {}) {
  const resolvedJobId = resolveJobId(jobId);
  const meta = fileMetadata(sourceItem, 0);

  if (!resolvedJobId) {
    return errorResult("JOB_ID_REQUIRED", "A DocWriter job ID is required.");
  }

  if (!meta.filename || !meta.contentType || meta.sizeBytes <= 0) {
    return errorResult(
      "INVALID_SOURCE",
      "The selected source file is missing its name, type, or size."
    );
  }

  return fetchJson(
    "POST",
    `/jobs/${encodeURIComponent(resolvedJobId)}/sources/upload-url`,
    {
      filename: meta.filename,
      contentType: meta.contentType,
      sizeBytes: meta.sizeBytes,
      requestId: safeString(options.requestId) || newRequestId("source"),
      ...(safeString(options.sourceId)
        ? { sourceId: safeString(options.sourceId) }
        : {}),
    },
    idToken
  );
}

export async function putDocwriterFileToSignedUrl(
  uploadTicket,
  file,
  {
    timeoutMs = DEFAULT_UPLOAD_TIMEOUT_MS,
    onProgress,
    progressContext = {},
    signal,
  } = {}
) {
  const ticketEnvelope = safeObject(uploadTicket);
  const ticket = safeObject(
    ticketEnvelope.upload || ticketEnvelope.ticket || ticketEnvelope
  );
  const uploadUrl = safeString(
    ticket.uploadUrl || ticket.upload_url || ticket.url
  );
  const method = safeString(
    ticket.uploadMethod || ticket.method || "PUT"
  ).toUpperCase();
  const requiredHeaders = safeObject(
    ticket.requiredHeaders || ticket.required_headers || ticket.headers
  );

  if (!uploadUrl) {
    return errorResult("UPLOAD_URL_MISSING", "The signed upload URL is missing.");
  }

  if (!file) {
    return errorResult("FILE_MISSING", "The selected source file is missing.");
  }

  if (typeof XMLHttpRequest === "undefined") {
    return errorResult(
      "UPLOAD_NOT_SUPPORTED",
      "This browser cannot report secure upload progress."
    );
  }

  const totalBytes = Math.max(0, Number(file.size || 0));
  const uploadStartedAtMs = uploadNowMs();
  const uploadStartedAtIso = new Date().toISOString();
  const hasContentTypeHeader = Object.keys(requiredHeaders).some(
    (name) => safeString(name).toLowerCase() === "content-type"
  );

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest();
    let settled = false;

    const cleanup = () => {
      if (
        signal &&
        typeof signal.removeEventListener === "function"
      ) {
        signal.removeEventListener("abort", abortUpload);
      }
    };

    const finish = (result) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const emitProgress = ({
      loadedBytes,
      reportedTotalBytes,
      lengthComputable,
      forceComplete = false,
    }) => {
      const elapsedMs = uploadElapsedMs(uploadStartedAtMs);
      const loaded = forceComplete
        ? totalBytes
        : Math.max(0, Number(loadedBytes || 0));
      const reportedTotal = Math.max(
        0,
        Number(reportedTotalBytes || totalBytes || 0)
      );
      const denominator = reportedTotal || totalBytes;
      const percent = forceComplete
        ? 100
        : denominator > 0
        ? Math.max(0, Math.min(100, Math.round((loaded / denominator) * 100)))
        : 0;
      const averageBytesPerSecond =
        elapsedMs > 0 ? Math.round((loaded * 1000) / elapsedMs) : 0;

      if (typeof onProgress === "function") {
        try {
          onProgress({
            ...safeObject(progressContext),
            type: "upload_progress",
            phase: "uploading_file",
            loadedBytes: loaded,
            totalBytes: denominator,
            sizeBytes: totalBytes,
            lengthComputable: Boolean(lengthComputable || denominator > 0),
            percent,
            elapsedMs,
            averageBytesPerSecond,
            startedAt: uploadStartedAtIso,
            fileStartedAt:
              safeString(progressContext.fileStartedAt) || uploadStartedAtIso,
          });
        } catch (error) {
          console.warn("[DocWriter API] Upload progress handler failed:", error);
        }
      }

      return {
        elapsedMs,
        averageBytesPerSecond,
      };
    };

    const abortUpload = () => {
      try {
        xhr.abort();
      } catch {
        finish(errorResult("UPLOAD_ABORTED", "The source upload was cancelled."));
      }
    };

    try {
      xhr.open(method, uploadUrl, true);
      xhr.timeout = Math.max(
        1,
        Number(timeoutMs || DEFAULT_UPLOAD_TIMEOUT_MS)
      );
      xhr.responseType = "text";

      Object.entries(requiredHeaders).forEach(([name, value]) => {
        if (!safeString(name) || value === undefined || value === null) return;
        xhr.setRequestHeader(name, String(value));
      });

      if (!hasContentTypeHeader) {
        xhr.setRequestHeader(
          "Content-Type",
          safeString(file.type) || "application/octet-stream"
        );
      }
    } catch (error) {
      finish(
        errorResult(
          "UPLOAD_SETUP",
          error?.message || "The secure source upload could not be prepared."
        )
      );
      return;
    }

    xhr.upload.onprogress = (event) => {
      emitProgress({
        loadedBytes: event.loaded,
        reportedTotalBytes: event.lengthComputable ? event.total : totalBytes,
        lengthComputable: event.lengthComputable,
      });
    };

    xhr.onload = () => {
      const status = Number(xhr.status || 0);

      if (status >= 200 && status < 300) {
        const finalProgress = emitProgress({
          loadedBytes: totalBytes,
          reportedTotalBytes: totalBytes,
          lengthComputable: true,
          forceComplete: true,
        });

        finish({
          ok: true,
          data: {
            uploaded: true,
            objectPath: ticket.objectPath || ticket.object_path,
            sourceId: ticket.sourceId || ticket.source_id,
            elapsedMs: finalProgress.elapsedMs,
            averageBytesPerSecond: finalProgress.averageBytesPerSecond,
          },
        });
        return;
      }

      const detail = safeString(xhr.responseText).slice(0, 300);
      finish(
        errorResult(
          String(status || "UPLOAD_HTTP_ERROR"),
          DOCWRITER_GENERIC_UPLOAD_ERROR,
          {
            status,
            technicalMessage: `The source upload failed${
              status ? ` (HTTP ${status})` : ""
            }${detail ? `: ${detail}` : ""}.`,
          }
        )
      );
    };

    xhr.onerror = () => {
      finish(
        errorResult(
          "UPLOAD_NETWORK",
          DOCWRITER_GENERIC_UPLOAD_ERROR,
          {
            technicalMessage:
              "The secure source upload was interrupted by a network error.",
          }
        )
      );
    };

    xhr.ontimeout = () => {
      finish(
        errorResult("UPLOAD_TIMEOUT", DOCWRITER_GENERIC_UPLOAD_ERROR, {
          technicalMessage: "The source upload timed out.",
        })
      );
    };

    xhr.onabort = () => {
      finish(errorResult("UPLOAD_ABORTED", "The source upload was cancelled."));
    };

    if (signal) {
      if (signal.aborted) {
        finish(errorResult("UPLOAD_ABORTED", "The source upload was cancelled."));
        return;
      }
      if (typeof signal.addEventListener === "function") {
        signal.addEventListener("abort", abortUpload, { once: true });
      }
    }

    try {
      xhr.send(file);
    } catch (error) {
      finish(
        errorResult(
          "UPLOAD_START",
          DOCWRITER_GENERIC_UPLOAD_ERROR,
          {
            technicalMessage:
              error?.message || "The secure source upload could not be started.",
          }
        )
      );
    }
  });
}

export async function confirmDocwriterSourceUpload(jobId, sourceId, idToken) {
  const resolvedJobId = resolveJobId(jobId);
  const normalizedSourceId = safeString(sourceId);

  if (!resolvedJobId || !normalizedSourceId) {
    return errorResult(
      "SOURCE_CONFIRMATION_REQUIRED",
      "The DocWriter job ID and source ID are required."
    );
  }

  return fetchJson(
    "POST",
    `/jobs/${encodeURIComponent(resolvedJobId)}/sources/${encodeURIComponent(
      normalizedSourceId
    )}/complete`,
    {},
    idToken
  );
}

export async function uploadDocwriterSources(
  jobId,
  sourceItems,
  idToken,
  options = {}
) {
  const resolvedJobId = resolveJobId(jobId);
  const items = Array.isArray(sourceItems)
    ? sourceItems.filter((item) => item?.status !== "error")
    : [];
  const totalFiles = items.length;
  const batchStartedAtMs = uploadNowMs();
  const batchStartedAtIso = new Date().toISOString();

  if (!resolvedJobId) {
    return errorResult("JOB_ID_REQUIRED", "A DocWriter job ID is required.");
  }

  if (!items.length) {
    emitDocwriterUploadEvent(options, {
      type: "no_sources",
      phase: "no_sources",
      jobId: resolvedJobId,
      totalFiles: 0,
      uploadedCount: 0,
      percent: 0,
      loadedBytes: 0,
      totalBytes: 0,
      elapsedMs: 0,
    });

    return {
      ok: true,
      data: {
        jobId: resolvedJobId,
        uploaded: [],
        sourceCount: 0,
      },
    };
  }

  emitDocwriterUploadEvent(options, {
    type: "batch_start",
    phase: "preparing_sources",
    jobId: resolvedJobId,
    totalFiles,
    uploadedCount: 0,
    percent: 0,
    batchStartedAt: batchStartedAtIso,
  });

  const uploaded = [];

  for (let index = 0; index < items.length; index += 1) {
    const item = items[index];
    const meta = fileMetadata(item, index);
    const fileNumber = index + 1;
    const fileStartedAtMs = uploadNowMs();
    const fileStartedAtIso = new Date().toISOString();

    const baseEvent = {
      jobId: resolvedJobId,
      fileNumber,
      fileIndex: fileNumber,
      totalFiles,
      fileName: meta.filename,
      sizeBytes: meta.sizeBytes,
      totalBytes: meta.sizeBytes,
      uploadedCount: uploaded.length,
      fileStartedAt: fileStartedAtIso,
      batchStartedAt: batchStartedAtIso,
    };

    if (!meta.file) {
      const result = errorResult(
        "SOURCE_FILE_REQUIRED",
        `Please reselect "${meta.filename}". The new DocWriter upload flow requires the original browser file, not a previously generated URL.`,
        { index, filename: meta.filename }
      );

      emitDocwriterUploadEvent(options, {
        ...baseEvent,
        type: "file_error",
        phase: "source_file_missing",
        error: result.error,
        elapsedMs: uploadElapsedMs(fileStartedAtMs),
      });

      return uploadErrorWithContext(result, {
        fileName: meta.filename,
        fileNumber,
        totalFiles,
        failureStage: "source_file_missing",
      });
    }

    emitDocwriterUploadEvent(options, {
      ...baseEvent,
      type: "file_start",
      phase: "requesting_upload_url",
      percent: 0,
      loadedBytes: 0,
      averageBytesPerSecond: 0,
      elapsedMs: 0,
    });

    const ticketResponse = await requestDocwriterSourceUpload(
      resolvedJobId,
      { ...item, rawFile: meta.file },
      idToken,
      {
        requestId:
          safeString(options.requestIdPrefix) ||
          newRequestId(`source_${fileNumber}`),
      }
    );

    if (!ticketResponse.ok) {
      emitDocwriterUploadEvent(options, {
        ...baseEvent,
        type: "file_error",
        phase: "requesting_upload_url",
        error: ticketResponse.error,
        elapsedMs: uploadElapsedMs(fileStartedAtMs),
      });

      return uploadErrorWithContext(ticketResponse, {
        fileName: meta.filename,
        fileNumber,
        totalFiles,
        failureStage: "requesting_upload_url",
      });
    }

    const ticketEnvelope = safeObject(ticketResponse.data);
    const upload = safeObject(
      ticketEnvelope.upload || ticketEnvelope.ticket || ticketEnvelope
    );
    const sourceId = safeString(
      ticketEnvelope.sourceId ||
        ticketEnvelope.source_id ||
        upload.sourceId ||
        upload.source_id
    );

    if (!sourceId) {
      const result = errorResult(
        "SOURCE_ID_MISSING",
        "The upload reservation did not return a DocWriter source ID."
      );

      emitDocwriterUploadEvent(options, {
        ...baseEvent,
        type: "file_error",
        phase: "requesting_upload_url",
        error: result.error,
        elapsedMs: uploadElapsedMs(fileStartedAtMs),
      });

      return uploadErrorWithContext(result, {
        fileName: meta.filename,
        fileNumber,
        totalFiles,
        failureStage: "upload_url_response",
      });
    }

    emitDocwriterUploadEvent(options, {
      ...baseEvent,
      type: "phase_start",
      phase: "uploading_file",
      sourceId,
      percent: 0,
      loadedBytes: 0,
      averageBytesPerSecond: 0,
      elapsedMs: uploadElapsedMs(fileStartedAtMs),
    });

    let latestUploadProgress = {
      elapsedMs: 0,
      averageBytesPerSecond: 0,
    };

    const putResponse = await putDocwriterFileToSignedUrl(upload, meta.file, {
      timeoutMs: options.uploadTimeoutMs || DEFAULT_UPLOAD_TIMEOUT_MS,
      signal: options.signal,
      progressContext: {
        ...baseEvent,
        sourceId,
      },
      onProgress: (progress) => {
        latestUploadProgress = {
          elapsedMs: Number(progress.elapsedMs || 0),
          averageBytesPerSecond: Number(progress.averageBytesPerSecond || 0),
        };
        emitDocwriterUploadEvent(options, {
          ...baseEvent,
          ...progress,
          sourceId,
        });
      },
    });

    if (!putResponse.ok) {
      emitDocwriterUploadEvent(options, {
        ...baseEvent,
        type: "file_error",
        phase:
          putResponse.error?.code === "UPLOAD_TIMEOUT"
            ? "file_upload_timeout"
            : "uploading_file",
        sourceId,
        error: putResponse.error,
        elapsedMs: uploadElapsedMs(fileStartedAtMs),
        averageBytesPerSecond: latestUploadProgress.averageBytesPerSecond,
      });

      return uploadErrorWithContext(putResponse, {
        fileName: meta.filename,
        fileNumber,
        totalFiles,
        sourceId,
        failureStage:
          putResponse.error?.code === "UPLOAD_TIMEOUT"
            ? "file_upload_timeout"
            : "uploading_file",
      });
    }

    const uploadResult = safeObject(putResponse.data);
    const averageBytesPerSecond = Math.max(
      0,
      Number(
        uploadResult.averageBytesPerSecond ||
          latestUploadProgress.averageBytesPerSecond ||
          0
      )
    );

    emitDocwriterUploadEvent(options, {
      ...baseEvent,
      type: "phase_start",
      phase: "confirming_upload",
      sourceId,
      percent: 100,
      loadedBytes: meta.sizeBytes,
      averageBytesPerSecond,
      elapsedMs: uploadElapsedMs(fileStartedAtMs),
    });

    const confirmResponse = await confirmDocwriterSourceUpload(
      resolvedJobId,
      sourceId,
      idToken
    );

    if (!confirmResponse.ok) {
      emitDocwriterUploadEvent(options, {
        ...baseEvent,
        type: "file_error",
        phase: "confirming_upload",
        sourceId,
        percent: 100,
        loadedBytes: meta.sizeBytes,
        averageBytesPerSecond,
        error: confirmResponse.error,
        elapsedMs: uploadElapsedMs(fileStartedAtMs),
      });

      return uploadErrorWithContext(confirmResponse, {
        fileName: meta.filename,
        fileNumber,
        totalFiles,
        sourceId,
        failureStage: "confirming_upload",
      });
    }

    const confirmedJob = safeObject(confirmResponse.data?.job);

    uploaded.push({
      id: meta.clientId || sourceId,
      sourceId,
      name: meta.filename,
      size: meta.sizeBytes,
      type: meta.contentType,
      note: meta.note,
      status: "uploaded",
      job: confirmedJob,
    });

    emitDocwriterUploadEvent(options, {
      ...baseEvent,
      type: "file_complete",
      phase: "file_complete",
      sourceId,
      percent: 100,
      loadedBytes: meta.sizeBytes,
      averageBytesPerSecond,
      uploadedCount: uploaded.length,
      elapsedMs: uploadElapsedMs(fileStartedAtMs),
    });
  }

  emitDocwriterUploadEvent(options, {
    type: "batch_complete",
    phase: "batch_complete",
    jobId: resolvedJobId,
    totalFiles,
    uploadedCount: uploaded.length,
    percent: 100,
    elapsedMs: uploadElapsedMs(batchStartedAtMs),
    batchStartedAt: batchStartedAtIso,
  });

  return {
    ok: true,
    data: {
      jobId: resolvedJobId,
      uploaded,
      sourceCount: uploaded.length,
    },
  };
}

export async function removeDocwriterSource(jobId, sourceId, idToken) {
  const resolvedJobId = resolveJobId(jobId);
  const normalizedSourceId = safeString(sourceId);

  if (!resolvedJobId || !normalizedSourceId) {
    return errorResult(
      "SOURCE_ID_REQUIRED",
      "The DocWriter job ID and source ID are required."
    );
  }

  return fetchJson(
    "DELETE",
    `/jobs/${encodeURIComponent(resolvedJobId)}/sources/${encodeURIComponent(
      normalizedSourceId
    )}`,
    undefined,
    idToken
  );
}

/* =========================================================================
 * Queue D1 and AI revisions — JSON only, never streaming
 * ========================================================================= */

export async function generateDocwriterJob(jobId, options = {}, idToken) {
  const resolvedJobId = resolveJobId(jobId);

  if (!resolvedJobId) {
    return errorResult("JOB_ID_REQUIRED", "A DocWriter job ID is required.");
  }

  const response = await fetchJson(
    "POST",
    `/jobs/${encodeURIComponent(resolvedJobId)}/generate`,
    {
      requestId: safeString(options.requestId) || newRequestId("generate"),
      ...(safeString(options.preferredRegion)
        ? { preferredRegion: safeString(options.preferredRegion) }
        : {}),
    },
    idToken,
    { timeoutMs: options.timeoutMs || DEFAULT_JSON_TIMEOUT_MS }
  );

  if (!response.ok) return response;

  return {
    ok: true,
    data: normalizeJobEnvelope(response.data),
  };
}

/* Existing launcher import name retained. It now queues D1 and returns 202 JSON. */
export async function createFirstDraft(
  { jobId, sessionId, preferredRegion, requestId } = {},
  idToken
) {
  const resolvedJobId = resolveJobId({ jobId, sessionId });

  return generateDocwriterJob(
    resolvedJobId,
    { preferredRegion, requestId },
    idToken
  );
}

export async function createDocwriterRevision(
  jobId,
  { instructions, revisionInstruction, baseRevisionId, preferredRegion, requestId } = {},
  idToken
) {
  const resolvedJobId = resolveJobId(jobId);
  const rawBaseRevisionId = safeString(baseRevisionId);
  const normalizedBaseRevisionId = normalizeRevisionId(baseRevisionId);
  let normalizedInstructions = "";

  if (rawBaseRevisionId && !normalizedBaseRevisionId) {
    return errorResult(
      "INVALID_BASE_REVISION_ID",
      "The selected base revision ID is invalid."
    );
  }

  try {
    normalizedInstructions = normalizeRevisionInstruction(
      revisionInstruction || instructions
    );
  } catch (error) {
    return errorResult(
      "INVALID_REVISION_INSTRUCTIONS",
      error?.message || "Revision instructions are invalid."
    );
  }

  if (!resolvedJobId) {
    return errorResult("JOB_ID_REQUIRED", "A DocWriter job ID is required.");
  }

  if (!normalizedInstructions) {
    return errorResult(
      "REVISION_INSTRUCTIONS_REQUIRED",
      "Revision instructions are required."
    );
  }

  const response = await fetchJson(
    "POST",
    `/jobs/${encodeURIComponent(resolvedJobId)}/revisions`,
    {
      revisionInstruction: normalizedInstructions,
      ...(normalizedBaseRevisionId
        ? { baseRevisionId: normalizedBaseRevisionId }
        : {}),
      ...(safeString(preferredRegion)
        ? { preferredRegion: safeString(preferredRegion) }
        : {}),
      requestId: safeString(requestId) || newRequestId("revision"),
    },
    idToken
  );

  if (!response.ok) return response;

  return {
    ok: true,
    data: normalizeJobEnvelope(response.data),
  };
}

/* Existing editor import name retained. It now queues one AI revision. */
export async function applyRevision(
  {
    jobId,
    sessionId,
    text,
    instructions,
    baseRevisionId,
    preferredRegion,
    requestId,
  } = {},
  idToken
) {
  const resolvedJobId = resolveJobId({ jobId, sessionId });

  return createDocwriterRevision(
    resolvedJobId,
    {
      instructions: instructions || text,
      baseRevisionId,
      preferredRegion,
      requestId,
    },
    idToken
  );
}

export async function revise(
  {
    jobId,
    sessionId,
    instruction,
    selectionHtml,
    baseRevisionId,
    preferredRegion,
    requestId,
  } = {},
  idToken
) {
  const selectionText = htmlToPlainText(selectionHtml).slice(0, 2_000);
  const combined = [
    safeString(instruction),
    selectionText ? `Selected text:\n${selectionText}` : "",
  ]
    .filter(Boolean)
    .join("\n\n");

  return applyRevision(
    {
      jobId,
      sessionId,
      instructions: combined,
      baseRevisionId,
      preferredRegion,
      requestId,
    },
    idToken
  );
}

/* =========================================================================
 * Revision read/save and finalization
 * ========================================================================= */

export async function getDocwriterRevision(
  jobId,
  revisionId,
  idToken,
  {
    hydrateContent = true,
    includeText = false,
    timeoutMs = DEFAULT_REVISION_CONTENT_TIMEOUT_MS,
  } = {}
) {
  const resolvedJobId = resolveJobId(jobId);
  const normalizedRevisionId = normalizeRevisionId(revisionId);

  if (!resolvedJobId || !normalizedRevisionId) {
    return errorResult(
      "REVISION_ID_REQUIRED",
      "A valid DocWriter job ID and revision ID are required."
    );
  }

  const response = await fetchJson(
    "GET",
    `/jobs/${encodeURIComponent(resolvedJobId)}/revisions/${encodeURIComponent(
      normalizedRevisionId
    )}`,
    undefined,
    idToken
  );

  if (!response.ok) return response;

  try {
    const exactEnvelope = normalizeExactRevisionEnvelope(
      response.data,
      normalizedRevisionId
    );
    const hydratedEnvelope = hydrateContent
      ? await hydrateExactRevisionEnvelope(exactEnvelope, {
          includeText,
          timeoutMs,
        })
      : exactEnvelope;

    return {
      ok: true,
      data: {
        ...hydratedEnvelope,
        jobId: resolvedJobId,
      },
    };
  } catch (error) {
    const message =
      error?.message || `The ${normalizedRevisionId} revision could not be verified.`;
    const errorCode = /timed out|could not be read|empty html content/i.test(message)
      ? "REVISION_CONTENT_READ_FAILED"
      : "REVISION_POINTER_MISMATCH";

    return errorResult(
      errorCode,
      "The selected document version could not be opened. Please try again.",
      {
        jobId: resolvedJobId,
        revisionId: normalizedRevisionId,
        technicalMessage: message,
      }
    );
  }
}

export async function getRevision(
  {
    jobId,
    sessionId,
    revisionId,
    hydrateContent = true,
    includeText = false,
    timeoutMs = DEFAULT_REVISION_CONTENT_TIMEOUT_MS,
  } = {},
  idToken
) {
  return getDocwriterRevision(
    resolveJobId({ jobId, sessionId }),
    revisionId,
    idToken,
    {
      hydrateContent,
      includeText,
      timeoutMs,
    }
  );
}

export async function listRevisions(
  { jobId, sessionId, order = "desc", withContent = false } = {},
  idToken
) {
  const resolvedJobId = resolveJobId({ jobId, sessionId });
  const jobResponse = await getDocwriterJob(resolvedJobId, idToken);
  if (!jobResponse.ok) return jobResponse;

  const envelope = safeObject(jobResponse.data);
  const job = safeObject(envelope.job);
  const revisionState = safeObject(job.revisionState);
  const revisionIndex = revisionIndexFromEnvelope(envelope);
  const revisionRecords = safeObject(revisionIndex.revisions);
  const ids = availableRevisionIdsFromEnvelope(envelope);
  const orderedIds = order === "asc" ? ids : [...ids].reverse();
  const currentRevisionId = normalizeRevisionId(
    revisionIndex.currentRevisionId ||
      revisionState.currentRevisionId ||
      revisionIndex.latestRevisionId ||
      revisionState.latestRevisionId
  );
  const revisions = [];

  for (const revisionId of orderedIds) {
    const compactRecord = safeObject(revisionRecords[revisionId]);

    if (!withContent) {
      revisions.push({
        ...compactRecord,
        revisionId,
        current: revisionId === currentRevisionId,
      });
      continue;
    }

    const revisionResponse = await getDocwriterRevision(
      resolvedJobId,
      revisionId,
      idToken
    );

    if (!revisionResponse.ok) {
      return {
        ...revisionResponse,
        error: {
          ...safeObject(revisionResponse.error),
          revisionId,
        },
      };
    }

    revisions.push({
      ...compactRecord,
      ...safeObject(revisionResponse.data.revision),
      revisionId,
      current: revisionId === currentRevisionId,
      contentHtml: revisionResponse.data.contentHtml || "",
      contentText: revisionResponse.data.contentText || "",
    });
  }

  return {
    ok: true,
    data: {
      jobId: resolvedJobId,
      sessionId: envelope.sessionId,
      revisions,
      items: revisions,
      currentRevisionId: currentRevisionId || null,
      availableRevisionIds: ids,
      versionCount:
        Number(revisionIndex.versionCount ?? revisionState.versionCount ?? ids.length) ||
        ids.length,
      completedAiRevisionCount:
        Number(
          revisionIndex.completedAiRevisionCount ??
            revisionState.completedAiRevisionCount ??
            revisionState.count ??
            0
        ) || 0,
    },
  };
}

export async function saveDocwriterRevision(
  jobId,
  revisionId,
  { htmlContent, textContent, metadata, requestId } = {},
  idToken
) {
  const resolvedJobId = resolveJobId(jobId);
  const normalizedRevisionId = normalizeRevisionId(revisionId);
  const html = safeString(htmlContent);
  const text = safeString(textContent) || htmlToPlainText(html);

  if (!resolvedJobId || !normalizedRevisionId) {
    return errorResult(
      "REVISION_ID_REQUIRED",
      "The DocWriter job ID and revision ID are required."
    );
  }

  if (!html || !text) {
    return errorResult(
      "REVISION_CONTENT_REQUIRED",
      "Both HTML and plain-text revision content are required."
    );
  }

  return fetchJson(
    "POST",
    `/jobs/${encodeURIComponent(resolvedJobId)}/revisions/${encodeURIComponent(
      normalizedRevisionId
    )}/save`,
    {
      htmlContent: html,
      textContent: text,
      metadata: safeObject(metadata),
      requestId: safeString(requestId) || newRequestId("manual_save"),
    },
    idToken
  );
}

export async function saveRevision(
  {
    jobId,
    sessionId,
    revisionId,
    contentHtml,
    htmlContent,
    textContent,
    title,
    requestId,
  } = {},
  idToken
) {
  return saveDocwriterRevision(
    resolveJobId({ jobId, sessionId }),
    revisionId,
    {
      htmlContent: htmlContent || contentHtml,
      textContent,
      metadata: title ? { title } : {},
      requestId,
    },
    idToken
  );
}

export async function saveContent(
  {
    jobId,
    sessionId,
    revisionId,
    selectedRevisionId,
    contentHtml,
    title,
    requestId,
  } = {},
  idToken
) {
  const resolvedJobId = resolveJobId({ jobId, sessionId });
  const rawExplicitRevisionId = safeString(
    revisionId || selectedRevisionId
  );
  const explicitRevisionId = normalizeRevisionId(
    rawExplicitRevisionId
  );

  if (rawExplicitRevisionId && !explicitRevisionId) {
    return errorResult(
      "INVALID_REVISION_ID",
      "The selected DocWriter revision ID is invalid."
    );
  }

  if (explicitRevisionId) {
    return saveDocwriterRevision(
      resolvedJobId,
      explicitRevisionId,
      {
        htmlContent: contentHtml,
        metadata: title ? { title } : {},
        requestId,
      },
      idToken
    );
  }

  const jobResponse = await getDocwriterJob(resolvedJobId, idToken);
  if (!jobResponse.ok) return jobResponse;

  const envelope = safeObject(jobResponse.data);
  const revisionState = safeObject(safeObject(envelope.job).revisionState);
  const revisionIndex = revisionIndexFromEnvelope(envelope);
  const currentRevisionId = normalizeRevisionId(
    revisionIndex.currentRevisionId ||
      revisionState.currentRevisionId ||
      revisionIndex.latestRevisionId ||
      revisionState.latestRevisionId
  );

  return saveDocwriterRevision(
    resolvedJobId,
    currentRevisionId,
    {
      htmlContent: contentHtml,
      metadata: title ? { title } : {},
      requestId,
    },
    idToken
  );
}

export async function previewDocwriterPdf(
  jobId,
  {
    revisionId,
    selectedRevisionId,
    htmlContent,
    contentHtml,
    textContent,
    title,
    subject,
    metadata,
    requestId,
    timeoutMs = DEFAULT_PDF_PREVIEW_TIMEOUT_MS,
  } = {},
  idToken
) {
  const resolvedJobId = resolveJobId(jobId);
  const rawRevisionId = safeString(revisionId || selectedRevisionId);
  const normalizedRevisionId = normalizeRevisionId(rawRevisionId);

  const rawHtml =
    htmlContent !== undefined && htmlContent !== null
      ? htmlContent
      : contentHtml;
  const htmlWasSupplied =
    rawHtml !== undefined && rawHtml !== null;
  const textWasSupplied =
    textContent !== undefined && textContent !== null;

  const html = htmlWasSupplied ? safeString(rawHtml) : "";
  const text = textWasSupplied
    ? safeString(textContent)
    : html
    ? htmlToPlainText(html)
    : "";

  if (!resolvedJobId) {
    return errorResult(
      "JOB_ID_REQUIRED",
      "A DocWriter job ID is required for PDF preview."
    );
  }

  if (!rawRevisionId) {
    return errorResult(
      "REVISION_ID_REQUIRED",
      "The exact DocWriter revision ID is required for PDF preview."
    );
  }

  if (!normalizedRevisionId) {
    return errorResult(
      "INVALID_REVISION_ID",
      "The selected DocWriter revision ID is invalid."
    );
  }

  if (textWasSupplied && !htmlWasSupplied) {
    return errorResult(
      "PDF_PREVIEW_HTML_REQUIRED",
      "HTML content is required when plain-text preview content is supplied."
    );
  }

  if (htmlWasSupplied && (!html || !text)) {
    return errorResult(
      "PDF_PREVIEW_CONTENT_REQUIRED",
      "The current HTML and plain-text document content are required for PDF preview."
    );
  }

  const normalizedMetadata = {
    ...safeObject(metadata),
    ...(safeString(title) ? { title: safeString(title) } : {}),
    ...(safeString(subject) ? { subject: safeString(subject) } : {}),
  };

  const response = await fetchPdf(
    `/jobs/${encodeURIComponent(resolvedJobId)}/preview-pdf`,
    {
      requestId: safeString(requestId) || newRequestId("preview_pdf"),
      revisionId: normalizedRevisionId,
      ...(htmlWasSupplied
        ? {
            htmlContent: html,
            textContent: text,
          }
        : {}),
      ...(Object.keys(normalizedMetadata).length
        ? { metadata: normalizedMetadata }
        : {}),
    },
    idToken,
    { timeoutMs }
  );

  if (!response.ok) return response;

  const returnedJobId = safeString(response.data?.jobId);
  const returnedRevisionId = normalizeRevisionId(
    response.data?.revisionId
  );

  if (returnedJobId && returnedJobId !== resolvedJobId) {
    return errorResult(
      "PDF_PREVIEW_JOB_MISMATCH",
      "The backend returned a PDF preview for a different DocWriter job.",
      {
        expectedJobId: resolvedJobId,
        returnedJobId,
      }
    );
  }

  if (
    returnedRevisionId &&
    returnedRevisionId !== normalizedRevisionId
  ) {
    return errorResult(
      "PDF_PREVIEW_REVISION_MISMATCH",
      `The backend returned a PDF preview for a different revision than ${normalizedRevisionId}.`,
      {
        expectedRevisionId: normalizedRevisionId,
        returnedRevisionId,
      }
    );
  }

  return {
    ok: true,
    data: {
      ...safeObject(response.data),
      jobId: resolvedJobId,
      revisionId: normalizedRevisionId,
    },
  };
}

export async function previewPdf(
  {
    jobId,
    sessionId,
    revisionId,
    selectedRevisionId,
    contentHtml,
    htmlContent,
    textContent,
    title,
    subject,
    metadata,
    requestId,
    timeoutMs,
  } = {},
  idToken
) {
  return previewDocwriterPdf(
    resolveJobId({ jobId, sessionId }),
    {
      revisionId: revisionId || selectedRevisionId,
      contentHtml,
      htmlContent,
      textContent,
      title,
      subject,
      metadata,
      requestId,
      timeoutMs,
    },
    idToken
  );
}

const DOCWRITER_FINAL_FORMATS = new Set(["pdf", "docx", "text"]);

function normalizeFinalizationFormat(value) {
  const normalized = safeString(value).toLowerCase();
  return DOCWRITER_FINAL_FORMATS.has(normalized) ? normalized : "";
}

export async function finalizeDocwriterJob(
  jobId,
  {
    revisionId,
    htmlContent,
    contentHtml,
    textContent,
    format,
    metadata,
    requestId,
  } = {},
  idToken
) {
  const resolvedJobId = resolveJobId(jobId);
  const rawFinalRevisionId = safeString(revisionId);
  const normalizedFinalRevisionId = normalizeRevisionId(rawFinalRevisionId);
  const normalizedFormat = normalizeFinalizationFormat(format);
  const html = safeString(htmlContent || contentHtml);
  const text = safeString(textContent) || (html ? htmlToPlainText(html) : "");

  if (!resolvedJobId) {
    return errorResult("JOB_ID_REQUIRED", "A DocWriter job ID is required.");
  }

  if (!rawFinalRevisionId) {
    return errorResult(
      "REVISION_ID_REQUIRED",
      "The exact DocWriter revision ID is required for finalization."
    );
  }

  if (!normalizedFinalRevisionId) {
    return errorResult(
      "INVALID_REVISION_ID",
      "The selected DocWriter revision ID is invalid."
    );
  }

  if (!normalizedFormat) {
    return errorResult(
      "INVALID_FINAL_FORMAT",
      "The final document format must be pdf, docx, or text."
    );
  }

  if (!html || !text) {
    return errorResult(
      "FINAL_CONTENT_REQUIRED",
      "The exact revision HTML and plain-text content are required for finalization."
    );
  }

  const normalizedMetadata = {
    ...safeObject(metadata),
    format: normalizedFormat,
  };

  const response = await fetchJson(
    "POST",
    `/jobs/${encodeURIComponent(resolvedJobId)}/finalize`,
    {
      requestId: safeString(requestId) || newRequestId("finalize"),
      revisionId: normalizedFinalRevisionId,
      format: normalizedFormat,
      htmlContent: html,
      textContent: text,
      metadata: normalizedMetadata,
    },
    idToken
  );

  if (!response.ok) return response;

  const finalizedEnvelope = normalizeJobEnvelope(response.data);
  const finalizedJob = safeObject(finalizedEnvelope.job);
  const finalization = safeObject(finalizedJob.finalization);
  const returnedRevisionId = normalizeRevisionId(
    finalizedEnvelope.revisionId || finalization.selectedRevisionId
  );

  if (finalizedEnvelope.finalized !== true || finalizedEnvelope.locked !== true) {
    return errorResult(
      "FINALIZATION_NOT_CONFIRMED",
      "The backend did not confirm that the document was finalized and locked.",
      { raw: finalizedEnvelope }
    );
  }

  if (returnedRevisionId !== normalizedFinalRevisionId) {
    return errorResult(
      "FINALIZED_REVISION_MISMATCH",
      `The backend finalized a different revision than ${normalizedFinalRevisionId}.`,
      {
        expectedRevisionId: normalizedFinalRevisionId,
        returnedRevisionId: returnedRevisionId || undefined,
        raw: finalizedEnvelope,
      }
    );
  }

  return {
    ok: true,
    data: {
      ...finalizedEnvelope,
      revisionId: normalizedFinalRevisionId,
      format: normalizedFormat,
    },
  };
}

export async function publishNow(
  {
    jobId,
    sessionId,
    revisionId,
    contentHtml,
    htmlContent,
    textContent,
    format,
    subject,
    requestId,
  } = {},
  idToken
) {
  return finalizeDocwriterJob(
    resolveJobId({ jobId, sessionId }),
    {
      revisionId,
      contentHtml,
      htmlContent,
      textContent,
      format,
      metadata: subject ? { subject } : {},
      requestId,
    },
    idToken
  );
}

/* =========================================================================
 * Canonical DocWriter history
 * ========================================================================= */

function historyBoolean(value, fallback = false) {
  if (value === undefined || value === null || value === "") {
    return Boolean(fallback);
  }

  if (typeof value === "boolean") return value;

  const normalized = safeString(value).toLowerCase();
  if (["1", "true", "yes", "y", "on"].includes(normalized)) return true;
  if (["0", "false", "no", "n", "off"].includes(normalized)) return false;

  return Boolean(value);
}

function normalizeHistoryPage(value, fallback = 1) {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? Math.max(1, parsed) : fallback;
}

function requireHistorySessionId(value) {
  const sessionId = safeString(value);
  return sessionId
    ? { ok: true, sessionId }
    : errorResult(
        "SESSION_ID_REQUIRED",
        "A DocWriter history session ID is required."
      );
}

export async function listDocwriterHistory(options = {}) {
  const opts = safeObject(options);
  const page = normalizeHistoryPage(opts.page ?? 1, 1);
  const pageSize = normalizeHistoryPage(
    opts.pageSize ??
      opts.page_size ??
      opts.limit ??
      opts.maxResults ??
      8,
    8
  );

  const query = safeString(opts.query ?? opts.search);
  const status =
    safeString(
      opts.status ??
        opts.statusFilter ??
        opts.status_filter ??
        "all"
    ) || "all";
  const sort =
    safeString(
      opts.sort ??
        opts.sortKey ??
        opts.sort_key ??
        "newest"
    ) || "newest";
  const showAll = historyBoolean(
    opts.showAll ?? opts.show_all,
    false
  );
  const includeDeleted = historyBoolean(
    opts.includeDeleted ?? opts.include_deleted,
    false
  );

  // Updated history-page defaults:
  // - page_size is the number of visible rows, normally eight.
  // - lookahead=1 makes the backend inspect one extra row, normally nine total.
  // - include_stats=0 keeps Next/Previous page requests lightweight.
  const lookahead = historyBoolean(
    opts.lookahead ??
      opts.lookAhead ??
      opts.fetchNext ??
      opts.fetch_next,
    true
  );
  const includeStats = historyBoolean(
    opts.includeStats ?? opts.include_stats,
    false
  );

  const queryParams = new URLSearchParams();
  queryParams.set("page", String(page));
  queryParams.set("page_size", String(pageSize));

  // Compatibility alias for older deployments. This is only the visible page
  // size and is never an overall DocWriter history-record ceiling.
  queryParams.set("limit", String(pageSize));

  queryParams.set("lookahead", lookahead ? "1" : "0");
  queryParams.set("include_stats", includeStats ? "1" : "0");

  if (query) queryParams.set("query", query);
  queryParams.set("status", status);
  queryParams.set("sort", sort);
  queryParams.set("show_all", showAll ? "true" : "false");
  queryParams.set(
    "include_deleted",
    includeDeleted ? "true" : "false"
  );

  return fetchJson(
    "GET",
    `/history?${queryParams.toString()}`,
    undefined,
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}

export async function getDocwriterHistoryStats(options = {}) {
  const opts = safeObject(options);
  const showAll = historyBoolean(
    opts.showAll ?? opts.show_all,
    false
  );
  const includeDeleted = historyBoolean(
    opts.includeDeleted ?? opts.include_deleted,
    false
  );

  const queryParams = new URLSearchParams();
  queryParams.set("show_all", showAll ? "true" : "false");
  queryParams.set(
    "include_deleted",
    includeDeleted ? "true" : "false"
  );

  return fetchJson(
    "GET",
    `/history/stats?${queryParams.toString()}`,
    undefined,
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}

export async function getDocwriterHistory(sessionId, options = {}) {
  const required = requireHistorySessionId(sessionId);
  if (!required.ok) return required;

  const opts = safeObject(options);
  const includeDeleted = historyBoolean(
    opts.includeDeleted ?? opts.include_deleted,
    false
  );

  const queryParams = new URLSearchParams();
  queryParams.set(
    "include_deleted",
    includeDeleted ? "true" : "false"
  );

  return fetchJson(
    "GET",
    `/history/${encodeURIComponent(required.sessionId)}?${queryParams.toString()}`,
    undefined,
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}

export async function renameDocwriterHistory(
  sessionId,
  title,
  options = {}
) {
  const required = requireHistorySessionId(sessionId);
  if (!required.ok) return required;

  const cleanTitle = safeString(title);
  if (!cleanTitle) {
    return errorResult(
      "HISTORY_TITLE_REQUIRED",
      "Please provide a name for this document."
    );
  }
  if (cleanTitle.length > 140) {
    return errorResult(
      "HISTORY_TITLE_TOO_LONG",
      "Document names cannot exceed 140 characters."
    );
  }

  const opts = safeObject(options);

  return fetchJson(
    "PATCH",
    `/history/${encodeURIComponent(required.sessionId)}/rename`,
    {
      title: cleanTitle,
      name: cleanTitle,
      displayTitle: cleanTitle,
      display_title: cleanTitle,
    },
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}

export async function archiveDocwriterHistory(
  sessionId,
  options = {}
) {
  const required = requireHistorySessionId(sessionId);
  if (!required.ok) return required;

  const opts = safeObject(options);

  return fetchJson(
    "PATCH",
    `/history/${encodeURIComponent(required.sessionId)}/archive`,
    { archived: true },
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}

export async function restoreDocwriterHistory(
  sessionId,
  options = {}
) {
  const required = requireHistorySessionId(sessionId);
  if (!required.ok) return required;

  const opts = safeObject(options);

  return fetchJson(
    "PATCH",
    `/history/${encodeURIComponent(required.sessionId)}/restore`,
    {
      archived: false,
      deleted: false,
    },
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}

export async function deleteDocwriterHistory(
  sessionId,
  options = {}
) {
  const required = requireHistorySessionId(sessionId);
  if (!required.ok) return required;

  const opts = safeObject(options);

  return fetchJson(
    "DELETE",
    `/history/${encodeURIComponent(required.sessionId)}`,
    undefined,
    opts.idToken,
    {
      timeoutMs: opts.timeoutMs ?? DEFAULT_JSON_TIMEOUT_MS,
    }
  );
}


/* =========================================================================
 * Existing editor-facing helpers that need a later UI update
 * ========================================================================= */

export async function sources({ jobId, sessionId } = {}, idToken) {
  const response = await getDocwriterJob(
    resolveJobId({ jobId, sessionId }),
    idToken
  );

  if (!response.ok) return response;

  const job = safeObject(response.data.job);
  const sourceMap = safeObject(job.sources);
  const items = Object.values(sourceMap);

  return {
    ok: true,
    data: {
      jobId: response.data.jobId,
      sessionId: response.data.sessionId,
      sources: items,
      items,
    },
  };
}

export async function restoreRevision() {
  return errorResult(
    "NOT_SUPPORTED",
    "Revision restore is not exposed by the current authoritative DocWriter routes."
  );
}

export async function changeSubject() {
  return errorResult(
    "NOT_SUPPORTED",
    "Subject changes require a dedicated authoritative DocWriter route."
  );
}

export async function exportDoc() {
  return errorResult(
    "NOT_SUPPORTED",
    "Export must use the finalized artifact URLs returned by the DocWriter job."
  );
}

export async function downloadPublished() {
  return errorResult(
    "NOT_SUPPORTED",
    "Published-file download must use the finalized DocWriter artifact URL."
  );
}

export async function listPublished() {
  return errorResult(
    "NOT_SUPPORTED",
    "Use the DocWriter history API for completed documents."
  );
}

export async function getPublished() {
  return errorResult(
    "NOT_SUPPORTED",
    "Use the DocWriter history API for completed documents."
  );
}

export async function deletePublished() {
  return errorResult(
    "NOT_SUPPORTED",
    "Use the DocWriter history API to remove a history item."
  );
}

/* =========================================================================
 * One-call launcher workflow
 * ========================================================================= */

export async function launchDocwriter(
  {
    request,
    sourceItems = [],
    preferredRegion = "",
    onUploadEvent,
    uploadTimeoutMs = DEFAULT_UPLOAD_TIMEOUT_MS,
    signal,
  } = {},
  idToken
) {
  const totalFiles = Array.isArray(sourceItems)
    ? sourceItems.filter((item) => item?.status !== "error").length
    : 0;
  const eventOptions = {
    onUploadEvent,
    uploadTimeoutMs,
    signal,
  };

  emitDocwriterUploadEvent(eventOptions, {
    type: "workspace_start",
    phase: "creating_job",
    totalFiles,
    uploadedCount: 0,
    percent: 0,
  });

  const createResponse = await createDocwriterJob(request, idToken);

  if (!createResponse.ok) {
    emitDocwriterUploadEvent(eventOptions, {
      type: "launch_error",
      phase: "creating_job",
      totalFiles,
      uploadedCount: 0,
      error: createResponse.error,
    });
    return createResponse;
  }

  const created = createResponse.data;
  const jobId = created.jobId;
  const sessionId = created.sessionId;

  emitDocwriterUploadEvent(eventOptions, {
    type: "job_created",
    phase: "job_created",
    jobId,
    sessionId,
    totalFiles,
    uploadedCount: 0,
    percent: 0,
  });

  const uploadResponse = await uploadDocwriterSources(
    jobId,
    sourceItems,
    idToken,
    eventOptions
  );

  if (!uploadResponse.ok) {
    return {
      ...uploadResponse,
      partial: {
        jobId,
        sessionId,
        jobCreated: true,
        generationQueued: false,
      },
    };
  }

  emitDocwriterUploadEvent(eventOptions, {
    type: "generation_start",
    phase: "queueing_generation",
    jobId,
    sessionId,
    totalFiles,
    uploadedCount: Number(uploadResponse.data.sourceCount || 0),
    percent: 100,
  });

  const generateResponse = await generateDocwriterJob(
    jobId,
    { preferredRegion },
    idToken
  );

  if (!generateResponse.ok) {
    emitDocwriterUploadEvent(eventOptions, {
      type: "launch_error",
      phase: "queueing_generation",
      jobId,
      sessionId,
      totalFiles,
      uploadedCount: Number(uploadResponse.data.sourceCount || 0),
      error: generateResponse.error,
    });

    return {
      ...generateResponse,
      partial: {
        jobId,
        sessionId,
        jobCreated: true,
        sourceCount: uploadResponse.data.sourceCount,
        generationQueued: false,
      },
    };
  }

  emitDocwriterUploadEvent(eventOptions, {
    type: "queued",
    phase: "queued",
    jobId,
    sessionId,
    totalFiles,
    uploadedCount: Number(uploadResponse.data.sourceCount || 0),
    percent: 100,
    operationId:
      generateResponse.data.operationId ||
      generateResponse.data.operation_id ||
      generateResponse.data.generation?.operationId ||
      generateResponse.data.generation?.operation_id ||
      null,
  });

  return {
    ok: true,
    data: {
      ...generateResponse.data,
      jobId,
      sessionId,
      created: createResponse.data,
      uploads: uploadResponse.data,
      generation: generateResponse.data,
    },
  };
}
